import type { ExperimentConfig } from '@/types';

const experiment5Config: ExperimentConfig = {
  id: 'exp5',
  number: 5,
  name: 'Soaps and Detergents',
  description: 'Study surface-active agents: prepare soap from lard via saponification with 15% NaOH alcohol solution, synthesize sodium lauryl sulfate detergent from dodecanol and concentrated H₂SO₄, and compare properties (emulsification, hard water, pH, acid reaction).',
  estimatedTime: '180 minutes',
  learningObjectives: [
    'Understand the saponification reaction (glyceryl ester + 3 NaOH → glycerol + 3 sodium salts of fatty acids)',
    'Synthesize soap from 5.0 g lard or vegetable oil and 30.0 mL 15% NaOH alcohol stock solution in a 125 mL flask',
    'Prepare sodium lauryl sulfate detergent from 5.0 mL dodecanol + 2.0 mL concentrated H₂SO₄, neutralized with 15.0 mL 1 M Na₂CO₃',
    'Understand salting out: pour into 25.0 g NaCl dissolved in 150 mL water',
    'Compare properties of soaps vs. detergents in four tests: emulsification, hard water, pH, acid reaction',
    'Understand surfactants: hydrophilic (water-loving) head and lipophilic (oil-loving) tail, micelle formation',
    'Categorize surfactants: anionic, cationic, amphoteric, non-ionic',
    'Understand why detergents replaced soaps (acid stability, hard water tolerance)',
  ],
  safetyNotes: [
    'CAUTION: Wear Eye Protection!',
    'The alcoholic NaOH solution is highly caustic — it burns the skin and damages clothing. In case of skin contact, immediately wash with copious amounts of water',
    'Concentrated sulfuric acid is highly corrosive and causes severe damage to skin and clothing — add slowly while stirring in the fume hood',
    'Dodecanol is a non-halogenated solvent — dispose of appropriately',
    'Na₂CO₃ neutralization produces CO₂ gas (effervescence)',
  ],
  requiredEquipment: [
    'Large beaker for boiling water bath',
    'Hot plate',
    '125 mL flask (for saponification)',
    'Watch glass (to cover flask)',
    'Boiling chips',
    'Graduated cylinder',
    'Cheesecloth (several layers, for soap filtration)',
    '400 mL beaker (for salt solution)',
    '100 mL beaker (for detergent)',
    '250 mL beaker (for ice-salt bath)',
    'Glass stirring rod',
    'Vacuum filtration apparatus (for detergent)',
    '6-inch (15 cm) test tubes (9 for hard water + 3 for emulsification)',
    'Corks for test tubes',
    'Broad- and narrow-range indicator paper',
    'Lard or vegetable oil (5.0 g)',
    '15% NaOH alcohol stock solution (30.0 mL)',
    'NaCl (25.0 g)',
    'Dodecanol (C₁₂H₂₅OH, 5.0 mL)',
    'Concentrated H₂SO₄ (2.0 mL)',
    '1 M Na₂CO₃ (15.0 mL)',
    'Phenolphthalein indicator (4 drops)',
    'Liquid shortening or vegetable oil (for emulsification test)',
    '1% CaCl₂ solution',
    '1% MgCl₂ solution',
    '1% FeCl₃ solution',
    'Dilute (1 M) HCl',
    'Deionised water',
    'Ice',
    'Salt (10 g, for ice-salt bath)',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'soapMaking',
      title: 'Part I: Soap Preparation (Saponification)',
      equipment: ['Large beaker for water bath', 'Hot plate', '125 mL flask', 'Watch glass', 'Boiling chips', 'Graduated cylinder', 'Cheesecloth', '400 mL beaker'],
      initialState: {
        waterBath: {
          temperature: 25,
          isHeating: false,
          isBoiling: false
        },
        flask: {
          contents: null,
          hasLard: false,
          hasNaOH: false,
          reactionProgress: 0
        },
        soapProduct: {
          mass: null,
          collected: false
        }
      },
      steps: [
        {
          id: 1,
          title: 'Set Up Boiling Water Bath',
          shortDescription: 'Fill large beaker half way with water, add boiling chips, begin to boil on hotplate',
          handler: 'handleSetupWaterBath',
          infoContent: {
            title: 'Water Bath',
            content: 'Fill a large beaker half way with water, add a few boiling chips and begin to boil on a hotplate. The beaker must be large enough to fit your 125 mL flask inside.',
            whatHappens: 'A boiling water bath is prepared to provide consistent, gentle heating for the saponification reaction without direct flame contact.',
            observe: [
              'Fill the beaker halfway with water and add boiling chips to prevent bumping',
              'The beaker must be large enough to accommodate the 125 mL reaction flask',
              'Wait for the water to reach a rolling boil before proceeding',
            ],
          }
        },
        {
          id: 2,
          title: 'Measure NaOH/Alcohol Solution',
          shortDescription: 'Transfer 30.0 mL of 15% NaOH alcohol stock solution to a small beaker',
          handler: 'handleMeasureNaOH',
          infoContent: {
            title: 'NaOH/Alcohol Solution',
            content: 'Using a graduated cylinder, transfer **30.0 mL of 15% NaOH alcohol stock solution** to a small beaker. **CAUTION:** This solution is highly caustic — it burns the skin and damages clothing. Wear eye protection!',
            whatHappens: 'The 15% NaOH/alcohol solution is measured; the alcohol acts as a solvent to mix the aqueous NaOH with the hydrophobic fat during saponification.',
            observe: [
              'Measure exactly 30.0 mL using a graduated cylinder',
              'The solution is clear but highly caustic — avoid skin contact',
              'Wear eye protection at all times when handling this solution',
            ],
          }
        },
        {
          id: 3,
          title: 'Add Lard to Flask',
          shortDescription: 'Place about 5.0 g of lard or vegetable oil into a 125 mL flask',
          handler: 'handleWeighLard',
          infoContent: {
            title: 'Fats for Soap',
            content: 'Place about **5.0 g of lard or vegetable oil** into a **125 mL flask**. Lard contains glyceryl esters (triglycerides) that undergo hydrolysis with NaOH to yield glycerol and sodium salts of fatty acids (soap).',
            whatHappens: 'Lard or vegetable oil (triglycerides) is weighed and placed in the reaction flask as the fat source that will be hydrolyzed by NaOH to produce soap.',
            observe: [
              'Weigh approximately 5.0 g of lard or measure the equivalent volume of vegetable oil',
              'Lard is a solid white fat; vegetable oil is a liquid yellow oil',
              'Both contain triglycerides (glyceryl esters of fatty acids)',
            ],
          }
        },
        {
          id: 4,
          title: 'Add NaOH Solution to Flask',
          shortDescription: 'Add the prepared 30.0 mL NaOH/alcohol solution, add boiling chips, cover with watch glass',
          handler: 'handleAddNaOHSolution',
          validation: {
            requires: ['lardWeighed']
          },
          infoContent: {
            title: 'Saponification Setup',
            content: 'Add the prepared sodium hydroxide solution to the flask. Add several **boiling chips** and cover the flask with a **watch glass** to avoid splashing.',
            chemicalEquation: 'Glyceryl ester + 3 NaOH → Glycerol + 3 R-COO⁻ Na⁺ (soap)',
            whatHappens: 'The NaOH/alcohol solution is combined with the fat in the flask, initiating the saponification reaction where triglycerides are hydrolyzed into glycerol and sodium salts of fatty acids (soap).',
            observe: [
              'The NaOH solution and fat initially form two distinct layers',
              'Add boiling chips to prevent bumping during heating',
              'Cover with a watch glass to prevent loss of material from splashing',
            ],
          }
        },
        {
          id: 5,
          title: 'Heat in Water Bath (40 min)',
          shortDescription: 'Suspend flask 2 mm above boiling water for 40 minutes',
          handler: 'handleHeatReaction',
          validation: {
            requires: ['naohAdded', 'waterBathHot'],
          },
          infoContent: {
            title: 'Saponification Reaction',
            content: 'Heat the flask by suspending it **2 mm above a bath of boiling water** for **40 minutes**. As the water level in the bath falls, lower the flask so that the bottom always contacts the boiling water.',
            whatHappens: 'The fat and NaOH react over 40 minutes in the water bath; the alcohol solvent allows the immiscible fat and aqueous NaOH to mix, driving the saponification to completion.',
            observe: [
              'The two layers gradually merge into a single homogeneous phase as reaction proceeds',
              'The mixture thickens as soap forms over the 40-minute period',
              'Maintain contact with the boiling water by lowering the flask as water evaporates',
            ],
          }
        },
        {
          id: 6,
          title: 'Prepare NaCl Solution',
          shortDescription: 'While heating: dissolve 25.0 g NaCl in 150 mL water in 400 mL beaker (set ~25 mL aside)',
          handler: 'handlePrepareSalt',
          infoContent: {
            title: 'Salt Solution for Salting Out',
            content: 'While the saponification reaction is proceeding, prepare a salt solution by dissolving about **25.0 g of sodium chloride (NaCl)** in **150 mL of water** in a **400 mL beaker**. Set approximately **~25 mL aside** for rinsing.',
            whatHappens: 'A concentrated NaCl solution is prepared for the salting-out step, where the high ionic strength will cause the dissolved soap to precipitate out of solution.',
            observe: [
              'Dissolve 25.0 g NaCl completely in 150 mL water — stir until fully dissolved',
              'Set aside approximately 25 mL of the salt solution for rinsing the flask later',
              'Prepare this while the 40-minute saponification is in progress to save time',
            ],
          }
        },
        {
          id: 7,
          title: 'Pour into NaCl Solution',
          shortDescription: 'Remove flask, pour hot mixture into salt solution, rinse flask',
          handler: 'handleSaltOut',
          infoContent: {
            title: 'Salting Out',
            content: 'Remove the flask from the steam bath and quickly pour the hot reaction mixture into the salt solution. **Rinse the flask** with some of the salt solution set aside and add the rinse material to the beaker.',
            whatHappens: 'The hot reaction mixture is poured into the concentrated NaCl solution, where the high salt concentration decreases soap solubility and causes it to precipitate out (salting out).',
            observe: [
              'White, solid soap precipitates upon contact with the salt solution',
              'The soap floats to the surface or forms solid masses in the beaker',
              'Rinse the flask with reserved salt solution to recover all product',
            ],
          },
        },
        {
          id: 8,
          title: 'Stir and Strain',
          shortDescription: 'Stir for several minutes, strain through several layers of cheesecloth',
          handler: 'handleFilterSoap',
        },
        {
          id: 9,
          title: 'Wash Soap',
          shortDescription: 'Wash collected soap twice with 10 mL portions of ice-cold water',
          handler: 'handleWashSoap',
          infoContent: {
            title: 'Washing',
            content: 'Wash the collected soap **twice** with **10 mL portions of ice-cold water** to remove any residual alkali and salt.',
            whatHappens: 'Ice-cold water washes remove residual NaOH and NaCl from the soap product without dissolving significant amounts of the soap itself.',
            observe: [
              'Use ice-cold water to minimize soap dissolving during washing',
              'Wash twice with 10 mL portions, allowing each wash to drain through',
              'The wash water may appear slightly soapy but should become clearer with each wash',
            ],
          }
        },
        {
          id: 10,
          title: 'Collect Soap',
          shortDescription: 'Twist cheesecloth to remove excess water, describe appearance/colour/texture/odour',
          handler: 'handleCollectSoap',
          infoContent: {
            title: 'Collecting the Product',
            content: '**Twist the cheesecloth** to remove the excess water from the soap. Describe your prepared soap in terms of its **appearance, colour, texture, and odour**. Record your observation. Set aside for use in Part III.',
            whatHappens: 'The cheesecloth is twisted to squeeze out excess water, yielding the final soap product for characterization and property testing.',
            observe: [
              'Twist the cheesecloth firmly to press out as much water as possible',
              'Note the appearance: typically white to off-white, soft, waxy solid',
              'Record the texture (smooth or grainy) and any odour',
              'Set the product aside for property testing in Part III',
            ],
          },
        }
      ]
    },
    {
      id: 2,
      name: 'detergentMaking',
      title: 'Part II: Detergent Preparation',
      equipment: ['100 mL beaker', '250 mL beaker (ice-salt bath)', 'Glass rod', 'Vacuum filtration apparatus'],
      initialState: {
        dodecanolAdded: false,
        sulfuricAcidAdded: false,
        indicatorAdded: false,
        isNeutralized: false,
        detergentProduct: {
          mass: null,
          collected: false
        }
      },
      steps: [
        {
          id: 1,
          title: 'Add Dodecanol',
          shortDescription: 'Place 5.0 mL dodecanol (C₁₂H₂₅OH) into a 100 mL beaker',
          handler: 'handleAddDodecanol',
          infoContent: {
            title: 'Starting Material',
            content: 'Place **5.0 mL dodecanol**, a fatty alcohol (C₁₂H₂₅OH), into a **100 mL beaker**. Dodecanol is a non-halogenated solvent — dispose of appropriately.',
            whatHappens: 'Dodecanol (lauryl alcohol), a 12-carbon fatty alcohol, is measured into a beaker as the starting material for synthesizing sodium lauryl sulfate detergent.',
            observe: [
              'Dodecanol is a clear to slightly yellow liquid with a faint waxy odour',
              'Measure exactly 5.0 mL into the 100 mL beaker',
              'Dodecanol is a non-halogenated solvent — dispose of in appropriate waste container',
            ],
          }
        },
        {
          id: 2,
          title: 'Add Concentrated H₂SO₄',
          shortDescription: 'Slowly add 2.0 mL concentrated H₂SO₄ in fume hood while stirring',
          handler: 'handleAddSulfuricAcid',
          validation: {
            requires: ['dodecanolAdded']
          },
          infoContent: {
            title: 'Esterification Reaction',
            content: `**CAUTION:** Concentrated sulfuric acid is highly corrosive — causes severe damage to skin and clothing. Wear eye protection!

While stirring with a glass rod, slowly add **2.0 mL of concentrated sulfuric acid** in the **fume hood**. Stir for **one minute** and allow to stand for **ten minutes**.`,
            chemicalEquation: 'CH₃(CH₂)₁₀CH₂OH + H₂SO₄ → CH₃(CH₂)₁₀CH₂-O-SO₂-OH + H₂O',
            whatHappens: 'Concentrated sulfuric acid esterifies the hydroxyl group of dodecanol, forming dodecyl hydrogen sulfate (an alkyl sulfuric acid ester).',
            observe: [
              'Add the H2SO4 slowly while stirring continuously in the fume hood',
              'The mixture may warm up as the reaction is exothermic',
              'Stir for one minute, then allow to stand for ten minutes for the reaction to complete',
            ],
          }
        },
        {
          id: 3,
          title: 'Prepare Ice-Salt Bath and Na₂CO₃',
          shortDescription: 'Prepare ice-salt bath (250 mL beaker, ice, 10 g salt, water to 75 mL); prepare 15.0 mL 1 M Na₂CO₃ + 4 drops phenolphthalein',
          handler: 'handlePrepareIceBath',
          validation: {
            requires: ['sulfuricAcidAdded']
          },
          infoContent: {
            title: 'Preparation',
            content: `**Ice-salt bath:** Fill a 250 mL beaker one-third full of ice; add about **10 g of salt** and enough water to bring the total volume to **75 mL**.

**Na₂CO₃ solution:** Place **15.0 mL of dilute (1 M) sodium carbonate** in a small beaker. Add **4 drops of phenolphthalein** indicator and mix well. (Phenolphthalein is colourless at pH < 8.3, pink at pH 8.3-10.0, and colourless again in strong alkaline solutions.)`,
            whatHappens: 'An ice-salt bath is prepared for cooling, and a sodium carbonate solution with phenolphthalein indicator is prepared for the neutralization step.',
            observe: [
              'The ice-salt bath should reach well below 0 C for effective cooling',
              'The Na2CO3 solution with phenolphthalein appears pink due to its basic pH',
              'The pink colour will disappear as the carbonate neutralizes the acid in the reaction mixture',
            ],
          }
        },
        {
          id: 4,
          title: 'Neutralize with Na₂CO₃',
          shortDescription: 'Slowly pour Na₂CO₃ into dodecanol-H₂SO₄ mixture, stir until pink disappears',
          handler: 'handleNeutralize',
          validation: {
            requires: ['indicatorAdded']
          },
          infoContent: {
            title: 'Acid-Base Neutralization',
            content: `Slowly and carefully pour the sodium carbonate solution into the dodecanol-sulfuric acid mixture. **Stir until the pink colour disappears.** A large amount of solid detergent should form after effervescence ceases.`,
            chemicalEquation: 'CH₃(CH₂)₁₀CH₂-O-SO₂-OH + NaOH → CH₃(CH₂)₁₀CH₂-O-SO₂-O⁻ Na⁺ + H₂O',
            whatHappens: 'Sodium carbonate neutralizes the excess sulfuric acid and converts the dodecyl hydrogen sulfate to sodium lauryl sulfate (the detergent); CO2 gas is released during neutralization.',
            observe: [
              'Vigorous effervescence (CO2 gas) occurs as carbonate reacts with the acid',
              'The pink phenolphthalein colour disappears as the base is consumed',
              'A large amount of solid white detergent forms after the effervescence ceases',
            ],
          }
        },
        {
          id: 5,
          title: 'Cool in Ice-Salt Bath',
          shortDescription: 'Pour mixture into ice-salt bath, stir vigorously to break up detergent',
          handler: 'handleCoolSolution',
          validation: {
            requires: ['isNeutralized']
          },
          infoContent: {
            title: 'Crystallization',
            content: 'Pour this mixture into the **ice-salt bath**. Stir vigorously to break up the detergent.',
            whatHappens: 'Cooling in the ice-salt bath causes more detergent to crystallize out of solution, increasing product yield.',
            observe: [
              'The detergent solidifies further upon cooling in the ice-salt bath',
              'Stir vigorously to break up large clumps into smaller pieces',
              'The white solid should become more abundant as the mixture cools',
            ],
          }
        },
        {
          id: 6,
          title: 'Filter and Wash',
          shortDescription: 'Filter by vacuum filtration, wash twice with 10 mL ice-cold water',
          handler: 'handleCollectDetergent',
          infoContent: {
            title: 'Product Collection',
            content: 'Filter the precipitated detergent mixture by **vacuum filtration** and wash the collected detergent in the funnel **twice with 10 mL portions of ice-cold water**. The washings remove residual acid and salt from the product.',
            whatHappens: 'Vacuum filtration separates the solid sodium lauryl sulfate detergent from the liquid, and ice-cold water washes remove residual acid and inorganic salts.',
            observe: [
              'The white detergent solid collects on the filter paper during vacuum filtration',
              'Wash twice with 10 mL portions of ice-cold water on the funnel',
              'The product should appear as a white or off-white solid after washing',
            ],
          }
        }
      ]
    },
    {
      id: 3,
      name: 'propertyTesting',
      title: 'Part III: Property Testing',
      equipment: ['6-inch (15 cm) test tubes', 'Corks', '1% CaCl₂', '1% MgCl₂', '1% FeCl₃', 'Vegetable oil/liquid shortening', '1 M HCl', 'Broad/narrow-range indicator paper', 'Ice bath'],
      initialState: {
        testResults: {
          emulsification: { soap: null, detergent: null },
          hardWater: { soap: null, detergent: null },
          pH: { soap: null, detergent: null },
          acidReaction: { soap: null, detergent: null }
        },
        currentTest: null
      },
      steps: [
        {
          id: 1,
          title: 'Prepare Solutions',
          shortDescription: 'Dissolve 1.0 g soap in 60 mL boiling deionized water; prepare similar detergent solution',
          handler: 'handlePrepareSolutions',
          infoContent: {
            title: 'Solution Preparation',
            content: 'Prepare a soap solution by dissolving **1.0 g** of the prepared soap in **60 mL of boiling deionized water**. Prepare a similar solution of detergent. Label each solution clearly and use each for the remaining experiments.',
            whatHappens: 'Soap and detergent are each dissolved in boiling deionised water to prepare solutions of similar concentration for comparative property testing.',
            observe: [
              'Soap may take some time to dissolve — boiling water speeds dissolution',
              'Both solutions should appear slightly translucent or hazy when dissolved',
              'Label the solutions clearly to avoid confusion during testing',
            ],
          }
        },
        {
          id: 2,
          title: 'A. Emulsification Test',
          shortDescription: 'Place 10 drops oil in three 6-inch test tubes, add 5 mL water/soap/detergent, shake',
          handler: 'handleEmulsificationTest',
          infoContent: {
            title: 'Emulsification',
            content: `Place **10 drops** of liquid shortening or vegetable oil into each of **three separate 6-inch (15 cm) test tubes**.

- **Tube 1:** add 5 mL deionized water
- **Tube 2:** add 5 mL soap solution
- **Tube 3:** add 5 mL detergent solution

Stopper each tube with a **cork** and shake vigorously, holding the cork in place with the thumb. Stop shaking and record the **minutes** it takes for the dispersion to form two distinct layers. The longer it takes, the greater the emulsification.`,
            whatHappens: 'Surfactant molecules surround oil droplets with their hydrophobic tails inward and hydrophilic heads outward, stabilizing an oil-in-water emulsion that resists separation.',
            observe: [
              'Water-only tube separates into two layers almost immediately',
              'Soap and detergent tubes maintain a cloudy emulsion for much longer',
              'Time how many minutes each tube takes to separate into two distinct layers',
              'Longer separation time indicates greater emulsifying ability',
            ],
          }
        },
        {
          id: 3,
          title: 'B. Hard Water Test',
          shortDescription: 'Nine test tubes: test soap/detergent/water with 1% CaCl₂, MgCl₂, FeCl₃',
          handler: 'handleHardWaterTest',
          infoContent: {
            title: 'Activity in Hard Water',
            content: `Clean **nine 6-inch (15 cm) test tubes** in three sets, each labelled 1, 2, 3.

- Tubes labelled 1: add 5 mL deionized water
- Tubes labelled 2: add 5 mL soap solution
- Tubes labelled 3: add 5 mL detergent solution

Add **2 mL of 1% calcium chloride (CaCl₂)** to one set (1, 2, 3). Mix and note whether precipitate forms.

Repeat with **1% magnesium chloride (MgCl₂)** and **1% iron(III) chloride (FeCl₃)**.

Indicate precipitate on a scale of **0-5** (0 = no precipitation, 5 = maximum).`,
            chemicalEquation: '2 CH₃(CH₂)₁₀COO⁻ + Ca²⁺ → Ca[CH₃(CH₂)₁₀COO]₂(s)',
            whatHappens: 'Hard water ions (Ca2+, Mg2+, Fe3+) form insoluble precipitates with soap (fatty acid salts) but generally do not precipitate with detergent (sulfonate salts), demonstrating a key advantage of detergents.',
            observe: [
              'Soap + hard water ions forms visible white precipitate (soap scum)',
              'Detergent + hard water ions produces little to no precipitate',
              'Rate precipitate on a 0-5 scale for each combination of surfactant and ion',
              'Water-only tubes serve as negative controls showing no precipitate',
            ],
          }
        },
        {
          id: 4,
          title: 'C. pH Test',
          shortDescription: 'Test soap and detergent solutions with broad- and narrow-range indicator paper',
          handler: 'handlePHTest',
          infoContent: {
            title: 'Acid-Base Reaction in Water (Hydrolysis)',
            content: `Test the aqueous soap and detergent solutions with **broad- and narrow-range indicator paper** to determine the approximate pH of each solution. Compare to the pH of deionized water.

Soaps are salts of strong bases and weak acids → basic/alkaline solutions. Detergent sulfonates behave differently.`,
            whatHappens: 'Soap solutions are basic (pH > 7) because soaps are salts of strong bases and weak fatty acids that undergo hydrolysis; detergent solutions may be closer to neutral.',
            observe: [
              'Soap solution shows a basic pH (typically pH 9-10) on indicator paper',
              'Detergent solution shows a pH closer to neutral (pH 7-8)',
              'Deionised water should show pH ~7 as a reference',
            ],
          }
        },
        {
          id: 5,
          title: 'D. Acid Reaction Test',
          shortDescription: 'Add 1 M HCl dropwise to 25 mL soap/detergent until pH 4.5, cool in ice bath',
          handler: 'handleAcidTest',
          infoContent: {
            title: 'Reaction with Mineral Acid',
            content: `Place **25 mL of soap solution** into a beaker. Add **dilute (1 M) HCl** dropwise until the contents reach a **pH of 4.5**. Cool the mixture in an **ice bath** for several minutes, and note the formation of any precipitate.

Repeat the procedure using **25.0 mL of detergent solution**.

In acidic solutions, soaps are converted to free fatty acids (insoluble).`,
            chemicalEquation: 'CH₃(CH₂)₁₀COONa(aq) + H₃O⁺ → CH₃(CH₂)₁₀COOH(s) + Na⁺ + H₂O(l)',
            whatHappens: 'Adding HCl to soap converts the soluble sodium salt to insoluble free fatty acid, which precipitates out; detergent sulfonate salts remain soluble in acidic conditions.',
            observe: [
              'Soap solution forms a white solid precipitate (free fatty acid) when acidified to pH 4.5',
              'The precipitate becomes more visible after cooling in the ice bath',
              'Detergent solution shows little or no precipitate under the same acidic conditions',
            ],
          }
        },
        {
          id: 6,
          title: 'Record All Observations',
          shortDescription: 'Record and discuss all results',
          handler: 'handleRecordObservations',
          infoContent: {
            title: 'Questions',
            content: `**Lab Questions:**
1. How do the polar groups found in most surfactants differ from each other?
2. What is the difference between ABS and LAS detergents?
3. Name two natural sources of glyceryl esters used in the preparation of soap.
4. When discussing detergents, what is meant by the term "inverted soap"?
5. What happened when you poured your oil, NaOH and alcohol solution into the NaCl solution?
6. What is the purpose of adding sodium carbonate solution to a dodecyl sulfate mixture?
7. Could sodium hydroxide solution be used in place of sodium carbonate solution? How should the two reactions differ?
8. Explain briefly why aqueous ammonia (NH₃) is an effective cleaning agent for greasy surfaces.
9. Show how you would make 100 mL of 0.0001 M HCl from a 10 M HCl stock solution.`,
            whatHappens: 'All observations from the four property tests are compiled to compare soap and detergent behaviour in emulsification, hard water, pH, and acidic conditions.',
            observe: [
              'Summarize which surfactant performed better in each test and explain why',
              'Detergents generally outperform soap in hard water and acidic conditions',
              'Soap and detergent show similar emulsification ability but differ in pH and acid stability',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp5',
    title: 'Soaps and Detergents Quiz',
    questions: [
      {
        id: 'exp5-q1',
        type: 'mcq' as const,
        question: 'What is saponification?',
        options: [
          { id: 'a', text: 'The reaction of an acid with a base' },
          { id: 'b', text: 'The hydrolysis of a fat/oil with NaOH to produce glycerol and soap' },
          { id: 'c', text: 'The dissolution of soap in water' },
          { id: 'd', text: 'The formation of micelles in solution' },
        ],
        correctOptionId: 'b',
        explanation: 'Saponification is the reaction: glyceryl ester + 3 NaOH → glycerol + 3 sodium salts of fatty acids (soap).',
      },
      {
        id: 'exp5-q2',
        type: 'mcq' as const,
        question: 'Why do detergents work better than soaps in hard water?',
        options: [
          { id: 'a', text: 'Detergents are stronger surfactants' },
          { id: 'b', text: 'Detergents do not form insoluble precipitates with Ca\u00B2⁺ and Mg\u00B2⁺ ions' },
          { id: 'c', text: 'Detergents are more concentrated' },
          { id: 'd', text: 'Detergents dissolve faster in water' },
        ],
        correctOptionId: 'b',
        explanation: 'Soaps form insoluble scum (calcium/magnesium salts of fatty acids) in hard water, reducing their effectiveness. Detergents like sodium lauryl sulfate remain soluble with hard water ions.',
      },
      {
        id: 'exp5-q3',
        type: 'short_answer' as const,
        question: 'Explain the structure of a surfactant molecule and how it enables cleaning.',
        sampleAnswer: 'A surfactant has a hydrophilic (water-loving) head group and a lipophilic (oil-loving) hydrocarbon tail. In solution, surfactants form micelles where the tails point inward and heads face outward. Oil and grease are trapped inside the micelle by the lipophilic tails, while the hydrophilic heads keep the micelle suspended in water, enabling removal of oily substances.',
      },
      {
        id: 'exp5-q4',
        type: 'mcq' as const,
        question: 'What is the purpose of NaCl in soap preparation ("salting out")?',
        options: [
          { id: 'a', text: 'To kill bacteria in the soap' },
          { id: 'b', text: 'To decrease the solubility of soap so it precipitates out of solution' },
          { id: 'c', text: 'To add fragrance' },
          { id: 'd', text: 'To neutralize excess NaOH' },
        ],
        correctOptionId: 'b',
        explanation: 'Adding NaCl (salting out) decreases the solubility of soap in the aqueous solution, causing it to precipitate as a solid that can be collected.',
      },
    ],
  },
};

export default experiment5Config;
